import type { InfiniteData } from '@tanstack/react-query';

import type { ReactionSummary, ReactionType } from '@/shared/components/reactions';

import type {
  ChatDetails,
  ChatListItem,
  ChatPage,
  Message,
  MessagePage,
  MessagePreview,
  ReactionGroup,
  UnreadSummary,
} from '../types';

/** Where a history window starts: the newest messages, a cursor, or a message to center on. */
export interface PageParam {
  before?: string;
  after?: string;
  around?: string;
}

export type MessagesData = InfiniteData<MessagePage>;

/** Every loaded message of a history window, oldest first. */
export function flattenMessages(data: MessagesData | undefined): Message[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

/** Whether the window reaches the newest message (realtime messages can be appended). */
export function isLatestWindow(data: MessagesData | undefined): boolean {
  const last = data?.pages[data.pages.length - 1];
  return !!last && last.newerCursor === null;
}

const sortKey = (message: Message) => (message.pending ? Number.MAX_SAFE_INTEGER : message.seq);

/**
 * Inserts a message (realtime or the server's answer to a send) or replaces the copy with the same id — or the
 * optimistic copy with the same client id. New messages are appended only to a window that reaches the newest message
 * (otherwise there would be a gap). Pending copies stay at the end.
 */
export function upsertMessage(data: MessagesData | undefined, message: Message): MessagesData | undefined {
  if (!data) return data;
  const sameMessage = (m: Message) =>
    m.id === message.id || (!!message.clientId && !!m.pending && m.clientId === message.clientId);
  const bySeq = (a: Message, b: Message) => sortKey(a) - sortKey(b);
  if (data.pages.some((page) => page.items.some(sameMessage))) {
    return {
      ...data,
      pages: data.pages.map((page) =>
        page.items.some(sameMessage)
          ? { ...page, items: page.items.map((m) => (sameMessage(m) ? message : m)).sort(bySeq) }
          : page,
      ),
    };
  }
  if (!isLatestWindow(data)) return data;
  const lastIndex = data.pages.length - 1;
  return {
    ...data,
    pages: data.pages.map((page, index) =>
      index === lastIndex ? { ...page, items: [...page.items, message].sort(bySeq) } : page,
    ),
  };
}

/** Applies `update` to the message with `id` wherever it is loaded. */
export function patchMessage(
  data: MessagesData | undefined,
  id: string,
  update: (message: Message) => Message,
): MessagesData | undefined {
  if (!data?.pages.some((page) => page.items.some((m) => m.id === id))) return data;
  return {
    ...data,
    pages: data.pages.map((page) =>
      page.items.some((m) => m.id === id)
        ? { ...page, items: page.items.map((m) => (m.id === id ? update(m) : m)) }
        : page,
    ),
  };
}

/** Drops a (failed optimistic) message. */
export function removeMessage(data: MessagesData | undefined, id: string): MessagesData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: page.items.filter((m) => m.id !== id) })),
  };
}

/** The chat list view of a message. */
export function previewOf(message: Message): MessagePreview {
  return {
    id: message.id,
    seq: message.seq,
    kind: message.kind,
    authorId: message.author?.id ?? null,
    authorName: message.author?.fullName ?? null,
    text: message.text,
    systemType: message.systemType,
    systemParams: message.systemParams,
    hasAttachments: message.attachments.length > 0,
    isDeleted: message.isDeleted,
    createdAt: message.createdAt,
  };
}

const byActivity = (a: ChatListItem, b: ChatListItem) =>
  new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();

// ---- the chat list: views and pages -----------------------------------------------------------------------------

/** The views of the list that are not a folder (`GET /api/chats/page?folder=`); anything else is a folder's id. */
export const BUILT_IN_VIEWS = ['all', 'unread', 'archive'] as const;
export type BuiltInView = (typeof BUILT_IN_VIEWS)[number];

export const isBuiltInView = (view: string): view is BuiltInView =>
  (BUILT_IN_VIEWS as readonly string[]).includes(view);

/** The data of a paged chat list (an infinite query of cursor pages). */
export type ChatListData = InfiniteData<ChatPage>;

/** Whether the chat counts as unread (a counter, or the person's own "mark as unread"). */
export const isUnreadChat = (chat: Pick<ChatListItem, 'unreadCount' | 'markedUnread'>): boolean =>
  chat.unreadCount > 0 || chat.markedUnread;

/** The server's rule of which chat is in which view (archived chats are only in the archive). */
export function belongsToView(view: string, chat: ChatListItem): boolean {
  switch (view) {
    case 'all':
      return !chat.isArchived;
    case 'unread':
      return !chat.isArchived && isUnreadChat(chat);
    case 'archive':
      return chat.isArchived;
    default:
      return !chat.isArchived && (chat.folderIds ?? []).includes(view);
  }
}

/** Every loaded chat of a paged list in order; a chat that two pages both carry (the list moved meanwhile) counts once. */
export function flattenChatPages(data: ChatListData | undefined): ChatListItem[] {
  if (!data) return [];
  const seen = new Set<string>();
  const items: ChatListItem[] = [];
  for (const page of data.pages)
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  return items;
}

/** Puts `items` back into the pages of `data`: every page keeps its size, the last one takes what is left over. */
function rechunkChatPages(data: ChatListData, items: ChatListItem[]): ChatListData {
  const last = data.pages.length - 1;
  let offset = 0;
  const pages = data.pages.map((page, index) => {
    const size = index === last ? items.length - offset : page.items.length;
    const slice = items.slice(offset, offset + Math.max(size, 0));
    offset += slice.length;
    return { ...page, items: slice };
  });
  return { ...data, pages };
}

/**
 * Applies an array helper (`applyMessageToList`, `applyReadToList`, …) to a paged list: the helper sees all loaded chats
 * as one list, its answer goes back into the pages. Like the helpers it returns `undefined` for a missing list, `null`
 * when the helper cannot tell (the caller refetches) and the same `data` when nothing changed.
 */
export function mapChatListPages(
  data: ChatListData | undefined,
  update: (items: ChatListItem[]) => ChatListItem[] | null | undefined,
): ChatListData | null | undefined {
  if (!data) return data;
  const items = flattenChatPages(data);
  const next = update(items);
  if (next === null) return null;
  if (next === undefined || next === items) return data;
  return rechunkChatPages(data, next);
}

/** Pins a chat (it goes on top: the newest pin first) or unpins it (it goes back among the others by its activity). */
export function setPinnedInList(list: ChatListItem[], chatId: string, pinned: boolean): ChatListItem[] {
  const item = list.find((c) => c.id === chatId);
  if (!item || item.isPinned === pinned) return list;
  const updated = { ...item, isPinned: pinned };
  const rest = list.filter((c) => c.id !== chatId);
  if (pinned) return [updated, ...rest];
  return [
    ...rest.filter((c) => c.isPinned),
    ...[updated, ...rest.filter((c) => !c.isPinned)].sort(byActivity),
  ];
}

/**
 * A new message in the chat list: preview, activity (the chat moves up — to the top of the unpinned chats, never above
 * the pinned ones) and unread counters. Returns `null` when the chat is not in the list (the caller refetches it).
 */
export function applyMessageToList(
  list: ChatListItem[] | undefined,
  message: Message,
  me: string,
  options: { readNow?: boolean } = {},
): ChatListItem[] | null | undefined {
  if (!list) return list;
  const item = list.find((c) => c.id === message.chatId);
  if (!item) return null;
  if (message.seq <= item.lastSeq && item.lastMessage?.id !== message.id) return list;
  const fromOther = message.author?.id !== me;
  const unread = fromOther && !options.readNow;
  const updated: ChatListItem = {
    ...item,
    lastMessage: previewOf(message),
    lastActivityAt: message.createdAt,
    lastSeq: Math.max(item.lastSeq, message.seq),
    lastReadSeq: fromOther && !options.readNow ? item.lastReadSeq : Math.max(item.lastReadSeq, message.seq),
    unreadCount: unread ? item.unreadCount + 1 : fromOther ? item.unreadCount : 0,
    unreadMentions:
      unread && message.mentionedUserIds.includes(me) ? item.unreadMentions + 1 : item.unreadMentions,
  };
  // Pinned chats keep their places (they are ordered by the pin, not by activity).
  if (item.isPinned) return list.map((c) => (c.id === item.id ? updated : c));
  const rest = list.filter((c) => c.id !== item.id);
  return [...rest.filter((c) => c.isPinned), ...[updated, ...rest.filter((c) => !c.isPinned)].sort(byActivity)];
}

/** An edited or deleted message updates the preview when it is the chat's last message. */
export function applyMessageUpdateToList(
  list: ChatListItem[] | undefined,
  message: Message,
): ChatListItem[] | undefined {
  if (!list) return list;
  return list.map((c) => (c.lastMessage?.id === message.id ? { ...c, lastMessage: previewOf(message) } : c));
}

/**
 * The current user read a chat up to `lastReadSeq`. Counters drop to zero when everything is read; returns `null`
 * when some messages remain unread (the exact counter needs the server).
 */
export function applyReadToList(
  list: ChatListItem[] | undefined,
  chatId: string,
  lastReadSeq: number,
): ChatListItem[] | null | undefined {
  if (!list) return list;
  const item = list.find((c) => c.id === chatId);
  if (!item) return list;
  if (lastReadSeq < item.lastSeq) return null;
  return list.map((c) =>
    c.id === chatId
      ? {
          ...c,
          lastReadSeq: Math.max(c.lastReadSeq, lastReadSeq),
          unreadCount: 0,
          unreadMentions: 0,
          markedUnread: false,
        }
      : c,
  );
}

/** An edited, deleted or re-reacted message in the pinned messages of a chat (a deleted one is unpinned by the server). */
export function applyMessageUpdateToPins(chat: ChatDetails, message: Message): ChatDetails {
  const pins = chat.pinnedMessages ?? (chat.pinnedMessage ? [chat.pinnedMessage] : []);
  if (!pins.some((m) => m.id === message.id)) return chat;
  const next = message.isDeleted
    ? pins.filter((m) => m.id !== message.id)
    : pins.map((m) => (m.id === message.id ? message : m));
  return { ...chat, pinnedMessages: next, pinnedMessage: next[0] ?? null };
}

/** The pinned messages of a chat, the newest pin first (older answers carry only the newest one). */
export function pinnedMessagesOf(chat: Pick<ChatDetails, 'pinnedMessage' | 'pinnedMessages'>): Message[] {
  if (chat.pinnedMessages && chat.pinnedMessages.length > 0) return chat.pinnedMessages;
  return chat.pinnedMessage ? [chat.pinnedMessage] : [];
}

/** One reaction per person: choosing the same again removes it, another one replaces it. */
export function toggleReactionGroups(
  groups: readonly ReactionGroup[],
  type: string,
  me: string,
): ReactionGroup[] {
  const mine = groups.find((g) => g.userIds.includes(me))?.type;
  const without = groups
    .map((g) => ({ ...g, userIds: g.userIds.filter((id) => id !== me) }))
    .filter((g) => g.userIds.length > 0);
  if (mine === type) return without;
  const existing = without.find((g) => g.type === type);
  return existing
    ? without.map((g) => (g.type === type ? { ...g, userIds: [...g.userIds, me] } : g))
    : [...without, { type, userIds: [me] }];
}

/** The reaction groups of a message as the shared `ReactionsBar` shows them. */
export function toReactionSummaries(groups: readonly ReactionGroup[], me: string): ReactionSummary[] {
  return groups.map((g) => ({
    type: g.type as ReactionType,
    count: g.userIds.length,
    reactedByMe: g.userIds.includes(me),
  }));
}

/**
 * The unread totals after a message from someone else arrived in `chatId` (the server's own rule: muted chats do not
 * count). Returns `null` when the summary cannot tell (an older answer without the chat lists): ask the server.
 */
export function countIncoming(summary: UnreadSummary, chatId: string): UnreadSummary | null {
  if (!Array.isArray(summary.unreadChatIds) || !Array.isArray(summary.mutedChatIds)) return null;
  if (summary.mutedChatIds.includes(chatId)) return summary;
  const known = summary.unreadChatIds.includes(chatId);
  return {
    ...summary,
    messages: summary.messages + 1,
    chats: known ? summary.chats : summary.chats + 1,
    unreadChatIds: known ? summary.unreadChatIds : [...summary.unreadChatIds, chatId],
  };
}
